/* @pareto-engineering/generator-front 1.0.12 */
/* eslint-disable import/no-extraneous-dependencies -- required here */
import * as React from 'react';
import { memo, useEffect } from 'react';
import { useField, useFormikContext } from 'formik';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import LexicalClickableLinkPlugin from '@lexical/react/LexicalClickableLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import PropTypes from 'prop-types';
import styleNames from '@pareto-engineering/bem/exports';

// Local Definitions

import { FormLabel, FormDescription } from "../../common";
import { ToolbarPlugin, TreeViewPlugin, StopPropagationPlugin } from "./common";
import "./styles.scss";
const baseClassName = styleNames.base;
const componentClassName = 'editor-input';
const ExposeEditorStatePlugin = ({
  setEditorState
}) => {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    setEditorState(editor);
  }, [editor]);
  return null;
};

/**
 * This is the component description.
 */
const EditorInput = ({
  id,
  className: userClassName,
  style,
  name,
  label,
  resize,
  color,
  rows,
  optional,
  labelColor,
  description,
  disabled,
  showDebugger,
  setEditorState
  // ...otherProps
}) => {
  const [field,,] = useField(name);
  const {
    setFieldValue
  } = useFormikContext();
  const setInitialValue = () => {
    try {
      JSON.parse(field.value);
      return field.value;
    } catch {
      const defaultValue = {
        root: {
          children: [{
            children: [{
              detail: 0,
              format: 0,
              mode: 'normal',
              style: '',
              text: '',
              type: 'text',
              version: 1
            }],
            direction: 'ltr',
            format: '',
            indent: 0,
            type: 'paragraph',
            version: 1
          }],
          direction: 'ltr',
          format: '',
          indent: 0,
          type: 'root',
          version: 1
        }
      };
      defaultValue.root.children[0].children[0].text = field.value;
      return JSON.stringify(defaultValue);
    }
  };
  const onChange = state => {
    setFieldValue(name, JSON.stringify(state));
  };
  const initialConfig = {
    nameSpace: name,
    editable: !disabled,
    editorState: setInitialValue(),
    theme: {
      text: {
        italic: 'italic',
        strikethrough: 'strikethrough',
        underline: 'underlined'
      },
      list: {
        listitemChecked: 'lexical-list-item-checked',
        listitemUnchecked: 'lexical-list-item-unchecked'
      },
      code: 'lexical-code'
    },
    nodes: [HeadingNode, QuoteNode, CodeNode, CodeHighlightNode, AutoLinkNode, LinkNode, ListNode, ListItemNode]
  };
  return /*#__PURE__*/React.createElement(LexicalComposer, {
    initialConfig: initialConfig
  }, /*#__PURE__*/React.createElement("div", {
    id: id,
    className: [baseClassName, componentClassName, userClassName, `y-${color}`, disabled && 'disabled'].filter(e => e).join(' '),
    style: {
      ...style,
      '--resize': resize,
      '--rows': `${rows}em`
    }
  }, /*#__PURE__*/React.createElement(FormLabel, {
    name: name,
    color: labelColor,
    optional: optional
  }, label), !disabled && /*#__PURE__*/React.createElement(ToolbarPlugin, null), /*#__PURE__*/React.createElement(RichTextPlugin, {
    contentEditable: /*#__PURE__*/React.createElement(ContentEditable, {
      className: "content-editable",
      id: name,
      name: name
    }),
    ErrorBoundary: LexicalErrorBoundary
  }), /*#__PURE__*/React.createElement(ListPlugin, null), /*#__PURE__*/React.createElement(CheckListPlugin, null), /*#__PURE__*/React.createElement(OnChangePlugin, {
    onChange: onChange
  }), /*#__PURE__*/React.createElement(LinkPlugin, null), /*#__PURE__*/React.createElement(LexicalClickableLinkPlugin, null), /*#__PURE__*/React.createElement(TabIndentationPlugin, null), setEditorState && /*#__PURE__*/React.createElement(ExposeEditorStatePlugin, {
    setEditorState: setEditorState
  }), /*#__PURE__*/React.createElement(HistoryPlugin, null), /*#__PURE__*/React.createElement(StopPropagationPlugin, null), /*#__PURE__*/React.createElement(FormDescription, {
    className: "s-1",
    description: description,
    name: name
  }), showDebugger && /*#__PURE__*/React.createElement(TreeViewPlugin, null)));
};
EditorInput.propTypes = {
  /**
   * The HTML id for this element
   */
  id: PropTypes.string,
  /**
   * The HTML class names for this element
   */
  className: PropTypes.string,
  /**
   * The React-written, css properties for this element.
   */
  style: PropTypes.objectOf(PropTypes.string),
  /**
   * The input name (html - and Formik state)
   */
  name: PropTypes.string.isRequired,
  /**
   * The input label
   */
  label: PropTypes.string.isRequired,
  /**
   * The number of rows int the text area
   */
  rows: PropTypes.number,
  /**
   * Color of the text
   */
  color: PropTypes.string,
  /**
   * Label base color
   */
  labelColor: PropTypes.string,
  /**
   * Text area description
   */
  description: PropTypes.string,
  /**
   * Whether the text area should be disabled
   */
  disabled: PropTypes.bool,
  /**
  * Whether the input is optional or not
  */
  optional: PropTypes.bool,
  /**
   * The resize property of the text area
   */
  resize: PropTypes.oneOf(['none', 'both', 'horizontal', 'vertical']),
  /**
   * Whether to show the debugger or not
   */
  showDebugger: PropTypes.bool
};
EditorInput.defaultProps = {
  rows: 10,
  disabled: false,
  color: 'paragraph',
  resize: 'vertical'
};
export default /*#__PURE__*/memo(EditorInput);